import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { orgMemberAvatarUrl } from '../../utils/githubAvatarUrl';
import { MemberHoverCard } from './MemberHoverCard';
import { memberAvatarBorder } from '../members/memberViewTheme';

const sizeClasses = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const iconSizes = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const sizeDimPx = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
};

/**
 * GitHub avatar for an org member (linked contributors or account GitHub), with fallback icon.
 * @param {{ member: Record<string, unknown>|null|undefined, size?: 'xs'|'sm'|'md'|'lg', className?: string }} props
 */
export default function OrgMemberAvatar({ member, size = 'md', className = '' }: any) {
  const [failed, setFailed] = useState(false);
  const src = orgMemberAvatarUrl(member);
  const box = sizeClasses[size] || sizeClasses.md;
  const iconCls = iconSizes[size] || iconSizes.md;
  const username = (member?.username && String(member.username)) || '';
  const alt = username ? `Avatar for ${username}` : 'Member avatar';

  if (!src || failed) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center rounded-full border-2 border-dark-border bg-dark-elevated text-vivid-green-cyan ${box} ${className}`}
        aria-hidden
      >
        <Users className={iconCls} />
      </span>
    );
  }

  const dim = sizeDimPx[size] || sizeDimPx.md;

  return (
    <img
      src={src}
      alt={alt}
      width={dim}
      height={dim}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className={`shrink-0 rounded-full object-cover bg-dark-elevated ${memberAvatarBorder} ${box} ${className}`}
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Org member avatar wrapped in a hover card with name, username, and role.
 * @param {{ member: Record<string, unknown>|null|undefined, size?: 'xs'|'sm'|'md'|'lg', className?: string }} props
 */
export function OrgMemberAvatarWithCard({ member, size = 'md', className = '' }: any) {
  return (
    <MemberHoverCard member={member}>
      <OrgMemberAvatar member={member} size={size} className={className} />
    </MemberHoverCard>
  );
}
